/**
 * 日焼け量の表示単位。
 *
 * 画面には常に2つ並べる：
 *   - 科学的な数値：SED（紅斑紫外線量。1 SED = 100 J/m²）
 *   - 感覚的な数値：P（1P = 真夏の晴天のプールに3時間、日焼け止めなしでいた場合）
 *
 * Pは小さい値だと「0.00P」になって実感が湧かないため、あわせて
 * 「プール◯分ぶん」という滞在時間の形でも出す。
 */
import { MED_SED, POOL_REFERENCE_HOURS, SED_PER_POOL } from "./constants.js";
import type { UserSettings } from "./types.js";

/** 1Pの説明文（凡例・ツールチップで使う共通文言） */
export const POOL_UNIT_NOTE =
  `1P＝真夏の晴天のプールに${POOL_REFERENCE_HOURS}時間、日焼け止めなしでいた場合の日焼け量です（概算）`;

/** SED → P */
export function sedToPool(sed: number): number {
  return sed / SED_PER_POOL;
}

/** SED → 同じ日焼けになるプール滞在時間 [分] */
export function sedToPoolMinutes(sed: number): number {
  return sedToPool(sed) * POOL_REFERENCE_HOURS * 60;
}

export interface SunburnLabel {
  /** 科学的な数値。例 "0.06 SED" */
  sed: string;
  /** 感覚的な数値。例 "0.01P未満" / "0.35P" / "1.2P" */
  p: string;
  /** 実感しやすい言い換え。例 "プール1分ぶん" / "プール3.2時間ぶん" */
  poolTime: string;
}

/** 日焼け量を科学値・P・プール換算の3表現に整形する */
export function describeSunburn(sed: number): SunburnLabel {
  const safe = Number.isFinite(sed) && sed > 0 ? sed : 0;
  const p = sedToPool(safe);
  const minutes = sedToPoolMinutes(safe);

  let pText: string;
  if (safe === 0) pText = "0P";
  else if (p < 0.01) pText = "0.01P未満";
  else if (p < 1) pText = `${p.toFixed(2)}P`;
  else pText = `${p.toFixed(1)}P`;

  let poolText: string;
  if (safe === 0) poolText = "日焼けなし";
  else if (minutes < 1) poolText = "プール1分未満";
  else if (minutes < 60) poolText = `プール${Math.round(minutes)}分ぶん`;
  else poolText = `プール${(minutes / 60).toFixed(1)}時間ぶん`;

  return {
    sed: `${safe < 10 ? safe.toFixed(2) : safe.toFixed(1)} SED`,
    p: pText,
    poolTime: poolText,
  };
}

/** 1行にまとめた表示。例 "0.06 SED ／ プール1分ぶん（0.01P未満）" */
export function formatSunburn(sed: number): string {
  const d = describeSunburn(sed);
  return `${d.sed} ／ ${d.poolTime}（${d.p}）`;
}

/**
 * 肌の限界（MED）に対する割合 [%]。医学的な意味づけとして P と併記する。
 * 100%で「日焼け（紅斑）が出はじめる量」。
 */
export function medPercent(sed: number, settings: Pick<UserSettings, "skinType">): number {
  return (sed / MED_SED[settings.skinType]) * 100;
}
